import React from "react";

import type { ClientId } from "../../domain/types";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

// What each templated step will collect once its real UI lands — from the
// design repo's onboarding-v2 flow (see docs/client-onboarding.md).
// The NAME is not repeated here: `domain/steps.ts` owns it and the page passes
// it in, so a rename reaches this screen too rather than leaving it quoting a
// step title nobody uses any more.
const STEP_BLURBS: Record<string, { description: string; collects: string[] }> = {
  "books-questions": {
    description: "Where this client's bookkeeping stands today.",
    collects: ["Bookkeeping type (existing Zoho / new with opening balances / new company / catch-up)", "Books backend system"],
  },
  "account-start-date": {
    description: "The month Zournal starts keeping this client's books.",
    collects: ["Account start date"],
  },
  "package-sizing": {
    description: "Size the Zoho plan from expected volume.",
    collects: ["Documents per year", "User count", "Multi-currency", "GSTIN count", "Recommended / chosen plan"],
  },
  "zoho-account": {
    description: "Whether the client already has a Zoho Books account.",
    collects: ["Has Zoho account (yes/no)", "New-account checklist"],
  },
  "add-zournal": {
    description: "Give Zournal admin access to the client's Zoho organisation.",
    collects: ["Admin user confirmation"],
  },
  "connect-tally": {
    description: "Download the Tally connector and read this client's books.",
    collects: ["Connector pairing", "Tally company name", "Chart of accounts, customers, vendors, and banks"],
  },
  "cfo-trial-balance": {
    description: "Upload the client's trial balance in Excel.",
    collects: ["Trial balance file", "Validation results"],
  },
  "readback-review": {
    description: "Confirm what we read back from the client's records.",
    collects: ["GSTINs", "Bank accounts"],
  },
  "gst-review": {
    description: "Review GST registrations read from the books.",
    collects: ["GSTIN list with state and status"],
  },
  "tds-tcs-review": {
    description: "Review TDS/TCS rates read from the books.",
    collects: ["TDS rates", "TCS rates"],
  },
  "banks-review": {
    description: "Confirm each bank account read from the books.",
    collects: ["Bank accounts (per-row confirm)"],
  },
  ledgers: {
    description: "Review the ledger structure read from the books.",
    collects: ["GL ledgers", "No-bills policy"],
  },
  "customers-vendors": {
    description: "Review customer and vendor masters.",
    collects: ["Contact patches"],
  },
  currency: {
    description: "Confirm the currencies in use.",
    collects: ["Currency settings"],
  },
  "opening-balances": {
    description: "Import opening balances and reconcile.",
    collects: ["Trial balance upload", "Invoices upload", "Ledger detail upload", "Reconciliation status"],
  },
  "prepare-zoho": {
    description: "Housekeeping run to prepare Zoho for Zournal.",
    collects: ["Housekeeping sequence status"],
  },
  "team-assignment": {
    description: "Who works on each module, and what waits for review.",
    collects: ["Makers and Checkers per module", "What each function does"],
  },
  "escalations-access": {
    description: "Who gets escalations and dashboard access.",
    collects: ["Escalation contacts", "Dashboard access"],
  },
  "dry-run": {
    description: "A trial month-close before going live.",
    collects: ["Dry-run results"],
  },
  "start-date": {
    description: "Set the go-live date - the one-way door.",
    collects: ["Live date", "Compliance setup"],
  },
};

export function PlaceholderStep({ clientId, slug, title }: { clientId: ClientId; slug: string; title: string }) {
  const { goNext } = useSubStepNav(clientId, slug);
  const blurb = STEP_BLURBS[slug];

  return (
    <SubStepShell
      title={title}
      description={blurb ? blurb.description : undefined}
      thenWhat="Continue → next step in the flow."
      thenWhatBelow
      onContinue={goNext}
    >
      <div className="flex flex-col gap-2 rounded-lg border border-dashed p-4">
        <span className="text-xs font-medium text-muted-foreground">Templated - real UI comes next</span>
        {blurb && (
          <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-muted-foreground">
            {blurb.collects.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    </SubStepShell>
  );
}
